import { ref, watch } from "vue";
import {
  getDivisionLabel,
  getHeaderDivisionBadgeLabel,
} from "../../utils/divisionLabels.ts";
import type { createDropdownOptionsModule } from "./dropdownOptionsModule.ts";
import type { createSearchFiltersModule } from "./searchFiltersModule.ts";

export function createHeaderBadgesModule(
  searchFilters: ReturnType<typeof createSearchFiltersModule>,
  dropdownOptions: ReturnType<typeof createDropdownOptionsModule>
) {
  const headerVendorBadgeValue = ref<string | undefined>(undefined);
  const headerDivisionBadgeValue = ref<string | undefined>(undefined);
  const headerWarehouseBadgeValue = ref<string | undefined>(undefined);

  function setHeaderVendorBadgeValue(vendorId: string | undefined): void {
    headerVendorBadgeValue.value = vendorId;
  }

  function setHeaderWarehouseBadgeValue(label: string | undefined): void {
    headerWarehouseBadgeValue.value = label;
  }

  function setHeaderDivisionBadgeValue(label: string | undefined): void {
    headerDivisionBadgeValue.value = label;
  }

  function clearSelections(): void {
    headerVendorBadgeValue.value = undefined;
    headerDivisionBadgeValue.value = undefined;
    headerWarehouseBadgeValue.value = undefined;
  }

  // Watchers that depend on searchFilters
  watch(searchFilters.vendorId, (newVendorId) => {
    setHeaderVendorBadgeValue(
      newVendorId?.length === 4 ? newVendorId : undefined
    );
  });

  watch(searchFilters.warehouseIds, (newWarehouseIds) => {
    if (newWarehouseIds.length === 0) {
      searchFilters.division.value = undefined;
      setHeaderWarehouseBadgeValue(undefined);
      setHeaderDivisionBadgeValue(undefined);
      return;
    }

    const normalizedWarehouseIds = newWarehouseIds.filter((warehouseId) =>
      warehouseId.includes("_")
    );
    const primaryWarehouseId = normalizedWarehouseIds[0];

    if (!primaryWarehouseId) {
      searchFilters.division.value = undefined;
      setHeaderWarehouseBadgeValue(undefined);
      setHeaderDivisionBadgeValue(undefined);
      return;
    }

    const warehouseLabels = normalizedWarehouseIds.map((warehouseId) => {
      const selectedWarehouse = dropdownOptions.warehouseOptions.value.find(
        (option) => option.value === warehouseId
      );
      return selectedWarehouse?.label ?? warehouseId;
    });
    setHeaderWarehouseBadgeValue(warehouseLabels.join(", "));

    const warehouseParts: string[] = primaryWarehouseId.split("_");
    const divisionAbbreviation: string | undefined = warehouseParts[0];
    searchFilters.division.value = divisionAbbreviation;

    const divisionLabels: string[] = [];
    for (const warehouseId of normalizedWarehouseIds) {
      const currentDivision = warehouseId.split("_")[0];
      if (!currentDivision) {
        continue;
      }
      const divisionLabel = getDivisionLabel(currentDivision);
      if (!divisionLabels.includes(divisionLabel)) {
        divisionLabels.push(divisionLabel);
      }
    }
    setHeaderDivisionBadgeValue(
      divisionLabels.length > 0 ? divisionLabels.join(", ") : undefined
    );
  });

  watch(searchFilters.division, (newDivision) => {
    if (searchFilters.warehouseIds.value.length > 0) {
      return;
    }

    setHeaderDivisionBadgeValue(getHeaderDivisionBadgeLabel(newDivision));
  });

  return {
    headerVendorBadgeValue,
    headerDivisionBadgeValue,
    headerWarehouseBadgeValue,
    setHeaderVendorBadgeValue,
    setHeaderWarehouseBadgeValue,
    setHeaderDivisionBadgeValue,
    clearSelections,
  };
}
